<template>
  <div id="page" class="container-fluid">
    <div class="row header mx-4 my-4">
      <div class="col">
        <h1>Episode Library</h1>
      </div>
      <b-btn @click.stop="statusPopoverShowing = !statusPopoverShowing"
             id="visit-statuses" variant="outline-orange mr-2">
        <font-awesome-icon class="orange mr-2" :icon="[ 'far', 'bars-progress' ]"/>Statuses
      </b-btn>
      <b-popover :show.sync="statusPopoverShowing"
                 target="visit-statuses" placement="bottomleft" triggers="">
        <b-form-group v-click-outside="hideStatusPopover" class="status-popover" label="Episode Status">
          <b-form-checkbox-group stacked v-model="selectedEpisodeStatuses" name="episodeStatuses" :options="episodeStatuses">
          </b-form-checkbox-group>
        </b-form-group>
      </b-popover>
      <button :disabled="bundlesNeedApproval === false" @click.prevent="approveAllBundles(true)" class="btn btn-orange mr-2">
        <font-awesome-icon class="mr-1" :icon="['far', 'check']"/>
        Approve All Episodes
      </button>
    </div>
  <div class="container-fluid">
    <div class="row data-table-header">
      <div class="col-1">Episode Codes</div>
      <div class="col-2 pl-5">Episode Name</div>
      <div class="col-1">Episode Term</div>
      <div class="col-3 pl-5">Description</div>
      <div class="col-1">Amount</div>
      <div class="col-1 pl-5">Status</div>
      <div class="col-3"></div>
    </div>

    <div v-for="bundle in employerBundles" v-if="bundle.status !=='Archived' && bundle.status !=='Draft'" class="row data-table-row" :key="bundle.organizationBundleId">
      <div class="column col-1"><b>{{ bundle.code }}</b>
        <div v-for="(code, index) in bundle.codes" v-if="bundle.codes" :key="'code-' + index">{{code.code}}</div>
      </div>
      <div class="column col-2 pl-5">{{ bundle.name }}</div>
      <div class="column col-1">{{ displayTerm(bundle)  }}</div>
      <div class="column col-3 pl-5">{{ bundle.description }}</div>
      <div class="column col-1">${{ formatNumber(bundle.amount) }}</div>
      <div class="column col-1 pl-5">{{ bundle.status === 'PendingApproval' ? 'Pending Approval' : bundle.status }}</div>
      <div class="col-3 text-right">
        <button v-if="bundle.status === 'PendingApproval'" @click.prevent="setBundleApproval(bundle, true)" class="btn btn-outline-orange m-1">
          <font-awesome-icon class="orange mr-1" :icon="['far', 'check']"/>
          Approve
        </button>
        <button v-if="bundle.status === 'PendingApproval'" @click.prevent="setBundleApproval(bundle, false)" class="btn btn-outline-orange m-1">
          <font-awesome-icon class="orange mr-1" :icon="['far', 'x']"/>
          Deny
        </button>
      </div>
    </div>
  </div>
    <confirm-modal @confirmed="approvalConfirmed" :title= confirmingModalTitle  class="align-middle"
                   confirmButton="Confirm"
                   :visible="showingConfirmModal"
                   @update:visible="visible => closeConfirmModal(visible)">
      <div class="row mb-3">
        <div class="col-2"></div>
        <div class="col-8 text-center">{{ confirmingModalBody }}</div>
        <div class="col-2"></div>
      </div>
    </confirm-modal>
  </div>
</template>

<script>
  import api from '@/services/api'
  import _ from 'lodash'
  import ConfirmModal from '@/components/ConfirmModal'
  import AnalyticsMixin from '@/mixins/AnalyticsMixin'

  export default {
    name: 'EmployerEpisodeLibrary',

    components: { ConfirmModal },

    mixins: [AnalyticsMixin],

    data () {
      return {
        employerBundles: [],
        employer: {
          organizationEmployerId: 0,
          name: '',
          paymentDistributions: []
        },
        statusPopoverShowing: false,
        episodeStatuses: [
          { text: 'Pending Approval', value: 2 },
          { text: 'Published', value: 3 },
          { text: 'Denied', value: 5 }
        ],
        selectedEpisodeStatuses: [2, 3, 5],
        showingConfirmModal: false,
        approving: true,
        confirmingBundle: {}
      }
    },

    watch: {
      selectedEpisodeStatuses: _.debounce(function (newValue, oldValue) { if (newValue !== oldValue) this.loadData() }, 500),
      currentPage (newValue, oldValue) {
        if (newValue !== oldValue) {
          this.loadData()
        }
      }
    },

    computed: {
      confirmingModalTitle () {
        if (this.confirmingBundle.organizationEmployerId) {
          if (this.approving) {
            return 'Approve Episode - ' + this.confirmingBundle.name
          } else {
            return 'Deny Episode - ' + this.confirmingBundle.name
          }
        } else {
          return 'Approve All Episodes'
        }
      },

      confirmingModalBody () {
        if (this.confirmingBundle.organizationEmployerId) {
          if (this.approving) {
            return 'Please confirm the episode may be published'
          } else {
            return 'Please confirm you wish to deny this episode. Once the episode has been denied a patient cannot choose this episode type.'
          }
        } else {
          return 'Please confirm all episodes may be published'
        }
      },

      bundlesNeedApproval () {
        const pendingBundles = this.employerBundles.filter(bundle => bundle.status === 'PendingApproval')
        if (pendingBundles.length > 0) {
          return true
        } else {
          return false
        }
      }
    },

    methods: {
      loadData () {
        this.isLoading = true
        const parameters = {
          statuses: this.selectedEpisodeStatuses
        }
        api.get('/Dashboard/Employer').then(response => {
          this.employer = response.data
          api.post('/Dashboard/Employer/Bundles', parameters).then(response => {
            this.employerBundles = response.data
            this.isLoading = false
          }).catch(error => {
            this.isLoading = false
            if (!this.$store.canHandleNetworkError(error)) {
              this.$store.logErrorResponse(this.$options.name, 'LoadError', error)
            }
          })
        }).catch(error => {
          this.isLoading = false
          if (!this.$store.canHandleNetworkError(error)) {
            this.setFromApiErrorResponse(error)
            this.$log.error('Error attempting to load employer details: ' + error.message, error)
          }
        })
      },

      approvalConfirmed (confirmed) {
        this.showingConfirmModal = false
        if (confirmed) {
          if (this.confirmingBundle.organizationEmployerId) {
            this.setBundleApproval(this.confirmingBundle, this.approving, true)
          } else {
            this.approveAllBundles(true, true)
          }
        } else {
          this.confirmingBundle = {}
        }
      },

      setBundleApproval (bundle, approved, confirmed = false) {
        if (!confirmed) {
          this.confirmingBundle = bundle
          this.approving = approved
          this.showingConfirmModal = true
          return
        }
        api.post('/Dashboard/Employer/Bundle/' + bundle.organizationBundleId + '/Approval/' + approved).then(response => {
          this.trackEvent('Bundle', 'Bundles', response.config.requestDuration)
          this.confirmingBundle = {}
          this.loadData()
        }).catch(error => {
          if (!this.$store.canHandleNetworkError(error)) {
            this.trackEvent('Bundle', 'BundlesError', error.message)
            this.$log.error('Error setting published status: ' + error.message, error)
            this.errors = ['Error updating bundle status: ' + error.message]
          }
        }).then(() => {
          // Workaround for https://github.com/bootstrap-vue/bootstrap-vue/issues/1826
          setTimeout(() => {
            this.showingLoadingModal = false
          }, 100)
        })
      },

      approveAllBundles (approved, confirmed = false) {
        if (approved && !confirmed) {
          this.showingConfirmModal = true
          return
        }
        api.post('/Dashboard/Employer/Bundles/ApproveAll').then(response => {
          this.trackEvent('Bundle', 'Bundles', response.config.requestDuration)
          this.loadData()
        }).catch(error => {
          if (!this.$store.canHandleNetworkError(error)) {
            this.trackEvent('Bundle', 'BundlesError', error.message)
            this.$log.error('Error setting published status: ' + error.message, error)
            this.errors = ['Error updating bundle status: ' + error.message]
          }
        }).then(() => {
          // Workaround for https://github.com/bootstrap-vue/bootstrap-vue/issues/1826
          setTimeout(() => {
            this.showingLoadingModal = false
          }, 100)
        })
      },

      formatNumber (number) {
        if (number) {
          return number.toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,')
        }

        return '0.00'
      },
      displayTerm (bundle) {
        if (bundle.durationDays === 0) {
          return 'DOS'
        } else {
          return bundle.durationDays + 'D'
        }
      },
      closeConfirmModal (visible) {
        if (!visible) {
          this.confirmingBundle = {}
          this.showingConfirmModal = false
        }
      },

      hideStatusPopover () {
        if (this.statusPopoverShowing) {
          this.$root.$emit('bv::hide::popover')
        }
      }
    },

    created () {
      this.loadData()
    }
  }

</script>
